import { CHAT_MAX_LENGTH, type ChatMessage, type PlayerId } from '@pixelworld/shared';
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { da } from '../../i18n/da';
import { useMediaQuery } from '../../lib/use-media-query';
import type { RoomChat } from '../rooms/useRoomConnection';
import { ChatRequests } from './ChatRequests';
import { mutedPlayers } from './muted-players';
import styles from './ChatBox.module.css';

const SHOWN_MESSAGES = 4;
const MESSAGE_VISIBLE_MS = 15_000;
const NOTICE_VISIBLE_MS = 6000;
/**
 * A phone played by touch, upright or on its side (the same sizes as RoomHud's): the text
 * field folds into a round button, so it does not lie across the island (2026-10-27).
 */
const PHONE_QUERY = '(pointer: coarse) and ((max-width: 640px) or (max-height: 500px))';

interface ShownMessage extends ChatMessage {
  key: number;
}

interface ChatBoxProps {
  chat: RoomChat;
  selfId: PlayerId;
  /** Everyone in a private room; the host and who they allowed in a public room. */
  canWrite: boolean;
  /** The host of a public room answers requests (ADR 0011). */
  isHost: boolean;
}

/**
 * Free text (ADR 0008, ADR 0011): a text field, or a button to ask the host for one, and
 * the latest messages, each with a button to hide that player. The game shows the same
 * messages in speech bubbles.
 */
export function ChatBox({ chat, selfId, canWrite, isHost }: ChatBoxProps) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [messages, setMessages] = useState<ShownMessage[]>([]);
  // Re-render when this viewer hides or shows a player.
  const [, setMutedVersion] = useState(0);
  useEffect(() => mutedPlayers.subscribe(() => setMutedVersion((value) => value + 1)), []);
  const phone = useMediaQuery(PHONE_QUERY);
  const [opened, setOpened] = useState(false);
  const folded = phone && !opened;
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  // Opened on a phone: straight into the text field, so the keyboard comes up.
  useEffect(() => {
    if (phone && opened) input.current?.focus();
  }, [phone, opened]);

  // The game's canvas cannot take focus, so a tap or click on it would leave the keys in the
  // text field. Let go of them as soon as the player touches anything outside the chat.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && box.current?.contains(event.target)) return;
      input.current?.blur();
      // A tap on the island folds a phone's text field away again, unless something is in it.
      if (!input.current?.value) setOpened(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, []);

  useEffect(() => {
    let key = 0;
    return chat.onMessage((message) => {
      key += 1;
      const shown = { ...message, key };
      setMessages((current) => [...current, shown].slice(-SHOWN_MESSAGES));
      setTimeout(
        () => setMessages((current) => current.filter((item) => item.key !== shown.key)),
        MESSAGE_VISIBLE_MS,
      );
    });
  }, [chat]);

  useEffect(
    () =>
      chat.onAnswer((outcome) => {
        setWaiting(false);
        setNotice(da.chat.answers[outcome]);
      }),
    [chat],
  );

  // The answer stays a moment; "waiting" stays until the answer comes.
  useEffect(() => {
    if (!notice || waiting) return;
    const timer = setTimeout(() => setNotice(null), NOTICE_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [notice, waiting]);

  const ask = async () => {
    setBusy(true);
    const result = await chat.request();
    setBusy(false);
    setWaiting(result.ok);
    setNotice(result.ok ? da.chat.waiting : da.chat.requestErrors[result.error]);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    const result = await chat.send(text);
    setBusy(false);
    if (result.ok) {
      setText('');
      setError(null);
      // Back to the game: the arrow keys walk again without a click first.
      input.current?.blur();
      setOpened(false);
    } else {
      setError(da.chat.errors[result.error]);
    }
  };

  // Typing must not move the player or trigger the game's keys.
  const keepKeysFromGame = (event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.currentTarget.blur();
      if (!text) setOpened(false);
    }
  };

  return (
    <div ref={box} className={styles.box}>
      {isHost && <ChatRequests chat={chat} />}
      <ul className={styles.messages} aria-live="polite" data-testid="chat-messages">
        {messages.map((message) => {
          const muted = mutedPlayers.has(message.playerId);
          return (
            <li key={message.key} className={styles.message}>
              {muted ? (
                <span className={styles.muted}>{da.chat.mutedNote(message.displayName)}</span>
              ) : (
                <span>
                  <strong>{message.displayName}:</strong> {message.text}
                </span>
              )}
              {message.playerId !== selfId && (
                <button
                  type="button"
                  className={styles.mute}
                  onClick={() => mutedPlayers.toggle(message.playerId)}
                >
                  {muted ? da.chat.unmute : da.chat.mute}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {folded && (
        <button
          type="button"
          className={styles.open}
          aria-label={canWrite ? da.chat.open : da.chat.ask}
          aria-expanded={false}
          onClick={() => setOpened(true)}
        >
          <PencilIcon />
        </button>
      )}
      {!canWrite && !folded && (
        <button
          type="button"
          className={styles.ask}
          disabled={busy || waiting}
          onClick={() => void ask()}
        >
          {da.chat.ask}
        </button>
      )}
      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}
      {canWrite && !folded && (
        <form className={styles.form} onSubmit={(event) => void submit(event)}>
          <input
            ref={input}
            className={styles.input}
            aria-label={da.chat.label}
            placeholder={da.chat.placeholder}
            value={text}
            maxLength={CHAT_MAX_LENGTH}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={keepKeysFromGame}
            onKeyUp={(event) => event.stopPropagation()}
            autoComplete="off"
            enterKeyHint="send"
          />
          <button type="submit" className={styles.send} disabled={busy || !text.trim()}>
            {da.chat.send}
          </button>
        </form>
      )}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** A pencil: write a message. */
function PencilIcon() {
  return (
    <svg className={styles.icon} viewBox="0 0 20 20" aria-hidden="true">
      <path d="M13.5 3.5l3 3L7 16H4v-3z" />
      <path d="M11.5 5.5l3 3" />
    </svg>
  );
}
